import { ActionIcon, Tooltip } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import commonClasses from "../styles/common.module.css";

/**
 * SCRUM-220 — a quiet "back" affordance in the app header.
 *
 * Several destinations are reached from a context they can't link back to. The
 * Requests page is the clearest case: it is opened from a resident's Forms tab
 * via "Request a form", but it is a group-home-level page with no resident in
 * scope, so there is no correct static "back to…" link to render — the only
 * thing that knows where you came from is the history stack.
 *
 * Deliberately NOT always visible. It renders only when there is an in-app
 * entry to return to, so a deep link or the first page after sign-in shows
 * nothing rather than an arrow that would eject you from the app. React Router
 * keys the first location in a session "default"; any later entry gets a
 * generated key, which makes that check exact and free — no history counter to
 * keep in sync, and it stays correct when the user walks back down the stack.
 *
 * Sits AFTER the logo rather than before it: the button is conditional, and
 * placing it first would shove the brand sideways whenever it appears.
 */
export function HeaderBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  if (location.key === "default") return null;

  return (
    <Tooltip label="Back" withArrow>
      <ActionIcon
        className={commonClasses.touchTarget}
        variant="subtle"
        color="gray"
        aria-label="Go back"
        onClick={() => navigate(-1)}
      >
        <IconArrowLeft size={18} />
      </ActionIcon>
    </Tooltip>
  );
}
